import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled, keyframes } from '@mui/system';
import { getOrganizations, assignUserToOrganization } from '../apis/Admin';
import { toast } from 'react-toastify';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const underlineGrow = keyframes`
  from { transform: scaleX(0); opacity: .2; }
  to   { transform: scaleX(1); opacity: 1; }
`;

const softPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(27,38,64,0.24); }
  70%  { box-shadow: 0 0 0 8px rgba(27,38,64,0.04); }
  100% { box-shadow: 0 0 0 0 rgba(27,38,64,0.0); }
`;

const TitleWrap = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  animation: `${fadeUp} 260ms ease-out both`,
  color: '#1B2640',
}));

const IconBadge = styled('div')(() => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, rgba(27,38,64,0.12), rgba(27,38,64,0.06))',
  border: `1px solid #1B2640`,
  animation: `${softPulse} 2.2s ease-out infinite`,
}));

const TitleText = styled('div')(() => ({
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#000',
  letterSpacing: 0.2,
  lineHeight: 1.2,
}));

const TitleUnderline = styled('div')(() => ({
  height: 5,
  width: '100%',
  transformOrigin: 'left',
  transform: 'scaleX(1)',
  marginTop: 6,
  background: `linear-gradient(90deg, #1B2640, transparent)`,
  animation: `${underlineGrow} 480ms 120ms ease-out both`,
  opacity: 0.85,
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    height: 45,
    '& fieldset': { border: '0.5px solid #e5e7eb' },
    '&:hover fieldset': { border: '0.5px solid #d1d5db' },
    '&.Mui-focused fieldset': { border: '0.6px solid #1B2640' },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#1B2640',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1B2640' },
  '& .MuiInputLabel-root.Mui-error': { color: '#d32f2f' },
}));

export default function AssignOrganizationDialog({
  open,
  user,
  onClose,
  onSuccess,
}) {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedOrgId('');
      setError('');
      return;
    }

    const fetchOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        toast.error(
          (typeof err?.response?.data === 'string' && err.response.data) ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load organizations',
        );
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrgs();
  }, [open]);

  const validate = () => {
    if (!selectedOrgId?.trim()) {
      setError('Please select an organization');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const userId = user?.user_id || user?.id;
    if (!userId) {
      toast.error('User ID is missing');
      return;
    }

    setSubmitting(true);
    try {
      await assignUserToOrganization(selectedOrgId.trim(), userId);
      toast.success('User assigned to organization successfully!');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to assign user to organization:', err);
      toast.error(
        (typeof err?.response?.data === 'string' && err.response.data) ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to assign user to organization',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getOrgName = (list, id) => {
    const norm = (v) =>
      String(v ?? '')
        .trim()
        .toLowerCase();
    const o = list.find(
      (x) => norm(x?.org_id ?? x?.orgId ?? x?.id) === norm(id),
    );
    return o?.org_name || o?.name || '';
  };

  const userName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.name || user?.email || 'User';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: (theme) => ({
          border: `1px solid #1B2640`,
          boxShadow: '0 6px 24px rgba(27,38,64,0.25)',
          backgroundColor: theme.palette.background.paper,
        }),
      }}
    >
      <DialogTitle sx={{ p: '16px 24px 8px' }}>
        <TitleWrap>
          <IconBadge>
            <BusinessRoundedIcon sx={{ color: '#1B2640', fontSize: 20 }} />
          </IconBadge>

          <Box sx={{ minWidth: 0 }}>
            <TitleText>Assign Organization</TitleText>
            <TitleUnderline />
          </Box>
        </TitleWrap>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: '#F8FAFF',
              border: '1px solid #E5E7EB',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#374151',
                mb: 0.5,
              }}
            >
              User Information
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
              <strong>Name:</strong> {userName}
            </Typography>
            {user?.email && (
              <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                <strong>Email:</strong> {user.email}
              </Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                select
                name="organization"
                label="Organization"
                value={String(selectedOrgId ?? '')}
                onChange={(e) => {
                  setSelectedOrgId(String(e.target.value ?? '').trim());
                  setError('');
                }}
                variant="outlined"
                fullWidth
                error={!!error}
                helperText={error || ' '}
                disabled={loadingOrgs || submitting}
                SelectProps={{
                  renderValue: (selected) => {
                    const id = String(selected ?? '').trim();
                    if (!id) return '';
                    return getOrgName(organizations, id) || id;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  {loadingOrgs ? 'Loading organizations...' : 'Select Organization'}
                </MenuItem>
                {organizations.map((org) => (
                  <MenuItem
                    key={org.org_id || org.id}
                    value={String(org.org_id || org.id).trim()}
                  >
                    {org.org_name || org.name || 'Unnamed Organization'}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{ color: '#1B2640' }}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={submitting || loadingOrgs || !selectedOrgId}
          sx={{
            bgcolor: '#1B2640',
            '&:hover': { bgcolor: '#0F1720' },
            '&:disabled': { bgcolor: '#9ca3af' },
          }}
          startIcon={
            submitting ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : null
          }
        >
          {submitting ? 'Assigning...' : 'Assign Organization'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AssignOrganizationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

